import axios from 'axios';
import { QueryRequest, QueryResponse, AgentExecutionFlow, ExecutionStatusResponse, SearchedOpinion, CivilLawsuitBucketResponse } from '../types';
import { HealthStatus } from '../types/health';
import { AvailableModelsResponse } from '../types/models';
import { EmbeddingIndexInfo } from '../types/embedding';
import { SystemPromptSummary, SystemPrompt, SystemPromptUsageResponse, SystemPromptScoreEntry } from '../types/systemPrompt';
import { ModelValidationRun, ModelValidationResult, SingleModelValidationRequest } from '../types/modelValidation';
import { authenticatedAxios, logout as logoutUtil } from './axiosConfig';
import type { TrendQueryDefinition } from '../types/trendQuery';

export interface TrendCategoryApiRecord {
  id: string;
  name: string;
  query: string;
  queryDefinition: TrendQueryDefinition;
  fields: string[];
  threshold: number;
  status: string;
  createdAt: string;
  totalChunks: number;
  completedChunks: number;
  totalMatches: number;
  backfillStartedAt?: string;
  backfillCompletedAt?: string;
  lastScoredAt?: string;
}

export interface TrendCategoryImportItem {
  query: string;
  queryDefinition: TrendQueryDefinition;
  name?: string;
  fields?: string[];
  threshold?: number;
}

export class ApiService {
  private static instance: ApiService;

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  async submitQuery(request: QueryRequest): Promise<QueryResponse> {
    try {
      const response = await authenticatedAxios.post<QueryResponse>(
        '/query', 
        request,
        { timeout: 120000 }
      );
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to submit query');
      }
      throw error;
    }
  }

  async submitQueryAsync(request: QueryRequest): Promise<{ executionId: string; message: string; userId?: string }> {
    try {
      const response = await authenticatedAxios.post<{ executionId: string; message: string; userId?: string }>(
        '/query/async', 
        request,
        { timeout: 10000 }
      );
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to submit query');
      }
      throw error;
    }
  }

  async getExecutionFlow(executionId: string): Promise<AgentExecutionFlow | null> {
    try {
      const response = await authenticatedAxios.get<AgentExecutionFlow>(
        `/agentexecution/${executionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getRecentExecutionFlows(limit: number = 50, userId?: string): Promise<AgentExecutionFlow[]> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (userId) {
        params.append('userId', userId);
      }

      const response = await authenticatedAxios.get<AgentExecutionFlow[]>(
        `/agentexecution?${params}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get execution flows');
      }
      throw error;
    }
  }

  async getExecutionStatus(executionId: string): Promise<ExecutionStatusResponse | null> {
    try {
      const response = await authenticatedAxios.get<ExecutionStatusResponse>(
        `/agentexecution/${executionId}/status`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getOpinionDetails(ids: number[]): Promise<SearchedOpinion[]> {
    try {
      const response = await authenticatedAxios.post<SearchedOpinion[]>(
        '/opinions/details', 
        ids
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get opinion details');
      }
      throw error;
    }
  }

  async getHealthStatus(): Promise<HealthStatus> {
    try {
      const response = await authenticatedAxios.get<HealthStatus>(
        '/health',
        {
          timeout: 10000,
          // Accept 2xx and 503 (starting up) as non-throwing; let 401/other errors throw via interceptor
          validateStatus: (status) => (status >= 200 && status < 300) || status === 503,
        }
      );
      // 503 means API is starting — throw so caller can handle it as a retriable state
      if (response.status === 503) {
        const err = new axios.AxiosError(
          'Service unavailable',
          'ERR_BAD_RESPONSE',
          response.config,
          response.request,
          response
        );
        throw err;
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw error;
    }
  }

  async getVersion(): Promise<{
    version: string;
    containerVersion: string;
    assemblyVersion: string;
    timestamp: string;
  }> {
    try {
      const response = await authenticatedAxios.get(
        '/health/version',
        { timeout: 5000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get version');
      }
      throw error;
    }
  }

  async getProviders(): Promise<any[]> {
    try {
      const response = await authenticatedAxios.get<any[]>(
        '/providers',
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get providers');
      }
      throw error;
    }
  }

  async getAvailableModels(): Promise<AvailableModelsResponse> {
    try {
      const response = await authenticatedAxios.get<AvailableModelsResponse>(
        '/providers/models',
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get available models');
      }
      throw error;
    }
  }

  async scoreResearchResults(request: {
    userPrompt: string;
    researchResults: Array<{
      clientId: string;
      answerText: string;
    }>;
  }): Promise<{
    scoredResults: Array<{
      clientId: string;
      accuracy: number;
      rank: number;
      reasoning: string;
      gapAnalysis?: string;
    }>;
    similarities: Array<{
      fromId: string;
      toId: string;
      score: number;
    }>;
    overallAnalysis?: string;
  }> {
    try {
      const response = await authenticatedAxios.post(
        '/researchscoring/score', 
        request,
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to score research results');
      }
      throw error;
    }
  }

  async getEmbeddingIndexes(): Promise<EmbeddingIndexInfo[]> {
    try {
      const response = await authenticatedAxios.get<EmbeddingIndexInfo[]>(
        '/embeddingindexes',
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get embedding indexes');
      }
      throw error;
    }
  }

  async getUserIdentity(): Promise<{
    email: string | null;
    isDevelopmentMode: boolean;
    isAuthenticated: boolean;
  }> {
    const response = await authenticatedAxios.get(
      '/useridentity',
      { timeout: 5000 }
    );
    return response.data;
  }

  async getSystemPrompts(includeInactive: boolean = false): Promise<SystemPromptSummary[]> {
    try {
      const params = new URLSearchParams();
      if (includeInactive) {
        params.append('includeInactive', 'true');
      }
      
      const response = await authenticatedAxios.get<SystemPromptSummary[]>(
        `/evaluation/system-prompts?${params}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompts');
      }
      throw error;
    }
  }

  async getSystemPromptsWithContent(): Promise<SystemPrompt[]> {
    try {
      const response = await authenticatedAxios.get<SystemPrompt[]>(
        '/evaluation/system-prompts/full',
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompts with content');
      }
      throw error;
    }
  }

  async getSystemPromptByHash(hash: string): Promise<SystemPrompt | null> {
    try {
      const response = await authenticatedAxios.get<SystemPrompt>(
        `/evaluation/system-prompts/${hash}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompt');
      }
      throw error;
    }
  }

  async getSystemPromptUsage(hash: string): Promise<SystemPromptUsageResponse> {
    try {
      const response = await authenticatedAxios.get<SystemPromptUsageResponse>(
        `/evaluation/system-prompts/${hash}/usage`,
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompt usage');
      }
      throw error;
    }
  }

  async getSystemPromptScores(): Promise<Record<string, SystemPromptScoreEntry>> {
    try {
      const response = await authenticatedAxios.get<Record<string, SystemPromptScoreEntry>>(
        '/evaluation/system-prompts/scores',
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompt scores');
      }
      throw error;
    }
  }

  // ==================== Model Validation ====================

  async runModelValidation(): Promise<ModelValidationRun> {
    try {
      const response = await authenticatedAxios.post<ModelValidationRun>(
        '/modelvalidation/run',
        {},
        { timeout: 300000 } // 5 min — many models to test
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to run model validation');
      }
      throw error;
    }
  }

  async runSingleModelValidation(request: SingleModelValidationRequest): Promise<ModelValidationResult> {
    try {
      const response = await authenticatedAxios.post<ModelValidationResult>(
        '/modelvalidation/run-single',
        request,
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to validate model');
      }
      throw error;
    }
  }

  async getModelValidationRuns(count: number = 20): Promise<ModelValidationRun[]> {
    try {
      const response = await authenticatedAxios.get<ModelValidationRun[]>(
        `/modelvalidation/runs?count=${count}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to get validation runs');
      }
      throw error;
    }
  }

  async searchCivilLawsuitsTrend(request: {
    query?: string;
    fields: string[];
    filters: {
      courtState?: string;
      civilLawsuitType?: string;
      natureOfSuit?: string;
      filedAtFrom?: string;
      filedAtTo?: string;
    };
  }): Promise<Array<{
    id: string;
    caseNumber: string;
    courtState: string;
    civilLawsuitType: string;
    natureOfSuit: string;
    filedAt: string;
    summary: string;
    matchedField: string;
    score: number;
  }>> {
    try {
      const response = await authenticatedAxios.post(
        '/search/civil-lawsuits/trends',
        {
          query: request.query || null,
          embeddingFields: request.fields,
          filters: request.filters
        }
      );
      // Backend returns { hits: [...], searchedFields: [...] }
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data?.hits && Array.isArray(data.hits)) {
        return data.hits.map((hit: any) => ({
          id: hit.id ?? hit.fields?.id ?? '',
          caseNumber: hit.fields?.caseNumber ?? '',
          courtState: hit.fields?.courtState ?? '',
          civilLawsuitType: hit.fields?.civilLawsuitType ?? '',
          natureOfSuit: hit.fields?.natureOfSuit ?? '',
          filedAt: hit.fields?.filedAt ?? '',
          summary: hit.fields?.summary ?? hit.fields?.fullText ?? '',
          matchedField: Object.keys(hit.fieldAttribution ?? {})[0] ?? '',
          score: hit.score ?? 0
        }));
      }
      return [];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to search civil lawsuits');
      }
      throw error;
    }
  }

  async getCivilLawsuitsHistogram(request: {
    query?: string;
    fields: string[];
    filters: {
      courtState?: string;
      courtStates?: string[];
      courtName?: string;
      judge?: string;
      civilLawsuitType?: string;
      natureOfSuit?: string;
      defendants?: string[];
      plaintiffLawyerFirms?: string[];
      filedAtFrom?: string;
      filedAtTo?: string;
    };
    interval: string;
    mode?: string;
    knnK?: number;
    knnNumCandidates?: number;
    maxChunks?: number;
    signal?: AbortSignal;
  }): Promise<{ buckets: Array<{ date: string; count: number }>; interval: string; totalCount: number; chunks?: Array<{ from: string; to: string; hitCount: number }> }> {
    const response = await authenticatedAxios.post(
      '/search/civil-lawsuits/trends/histogram',
      {
        query: request.query || null,
        embeddingFields: request.fields,
        filters: request.filters,
        interval: request.interval,
        mode: request.mode || 'vector',
        knnK: request.knnK || null,
        knnNumCandidates: request.knnNumCandidates || null,
        maxChunks: request.maxChunks || null
      },
      { signal: request.signal }
    );
    return response.data;
  }

  async getCivilLawsuitsBucket(request: {
    bucketDate: string;
    interval: string;
    query?: string;
    fields: string[];
    filters: {
      courtState?: string;
      courtName?: string;
      judge?: string;
      civilLawsuitType?: string;
      natureOfSuit?: string;
      filedAtFrom?: string;
      filedAtTo?: string;
    };
    mode?: string;
    page: number;
    pageSize: number;
  }): Promise<CivilLawsuitBucketResponse> {
    const response = await authenticatedAxios.post(
      '/search/civil-lawsuits/trends/bucket',
      {
        bucketDate: request.bucketDate,
        interval: request.interval,
        query: request.query || null,
        embeddingFields: request.fields,
        filters: request.filters,
        mode: request.mode || 'vector',
        page: request.page,
        pageSize: request.pageSize
      }
    );
    return response.data;
  }

  async getCivilLawsuitsList(request: {
    query?: string;
    fields: string[];
    filters: {
      courtState?: string;
      courtStates?: string[];
      courtName?: string;
      judge?: string;
      civilLawsuitType?: string;
      natureOfSuit?: string;
      defendants?: string[];
      plaintiffLawyerFirms?: string[];
      filedAtFrom?: string;
      filedAtTo?: string;
    };
    mode?: string;
    knnK?: number;
    knnNumCandidates?: number;
    maxChunks?: number;
    signal?: AbortSignal;
    page: number;
    pageSize: number;
  }): Promise<CivilLawsuitBucketResponse> {
    const response = await authenticatedAxios.post(
      '/search/civil-lawsuits/trends/list',
      {
        query: request.query || null,
        embeddingFields: request.fields,
        filters: request.filters,
        mode: request.mode || 'vector',
        knnK: request.knnK || null,
        knnNumCandidates: request.knnNumCandidates || null,
        maxChunks: request.maxChunks || null,
        page: request.page,
        pageSize: request.pageSize
      },
      { signal: request.signal }
    );
    return response.data;
  }

  // ==================== Pre-computed Trend Categories ====================

  async listTrendCategories(): Promise<TrendCategoryApiRecord[]> {
    const response = await authenticatedAxios.get<TrendCategoryApiRecord[]>('/civil-lawsuits/trend-categories');
    return response.data;
  }

  async syncTrendCategories(): Promise<void> {
    await authenticatedAxios.post('/civil-lawsuits/trend-categories/sync');
  }

  async syncTrendCategory(id: string): Promise<void> {
    await authenticatedAxios.post(`/civil-lawsuits/trend-categories/${id}/sync`);
  }

  async backfillTrendCategory(id: string): Promise<void> {
    await authenticatedAxios.post(`/civil-lawsuits/trend-categories/${id}/backfill`);
  }

  async getTrendCategory(id: string): Promise<TrendCategoryApiRecord> {
    const response = await authenticatedAxios.get<TrendCategoryApiRecord>(`/civil-lawsuits/trend-categories/${id}`);
    return response.data;
  }

  async createTrendCategory(request: {
    name: string;
    query: string;
    queryDefinition: TrendQueryDefinition;
    fields: string[];
    threshold: number;
  }): Promise<{ id: string }> {
    const response = await authenticatedAxios.post('/civil-lawsuits/trend-categories', request);
    return response.data;
  }

  async deleteTrendCategory(id: string): Promise<void> {
    await authenticatedAxios.delete(`/civil-lawsuits/trend-categories/${id}`);
  }

  async exportTrendCategories(): Promise<TrendCategoryImportItem[]> {
    const cats = await this.listTrendCategories();
    return cats.map(c => ({
      query: c.query,
      queryDefinition: c.queryDefinition,
      name: c.name,
      fields: c.fields,
      threshold: c.threshold
    }));
  }

  async importTrendCategories(items: TrendCategoryImportItem[]): Promise<void> {
    const defaults = { fields: ['causeOfActionEmbedding'], threshold: 0.7 };
    for (const item of items) {
      await this.createTrendCategory({
        name: item.name || item.query.slice(0, 60),
        query: item.query,
        queryDefinition: item.queryDefinition,
        fields: item.fields || defaults.fields,
        threshold: item.threshold ?? defaults.threshold
      });
    }
  }

  async getTrendCategoryHistogram(id: string, request: {
    filters: {
      courtStates?: string[];
      courtNames?: string[];
      defendants?: string[];
      plaintiffLawyerFirms?: string[];
      filedAtFrom?: string;
      filedAtTo?: string;
    };
    interval: string;
  }): Promise<{ buckets: Array<{ date: string; count: number }>; interval: string; totalCount: number }> {
    const response = await authenticatedAxios.post(
      `/civil-lawsuits/trend-categories/${id}/histogram`,
      {
        ...request.filters,
        interval: request.interval
      }
    );
    return response.data;
  }

  async getTrendCategoryCourts(id: string): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      `/civil-lawsuits/trend-categories/${id}/courts`
    );
    return response.data;
  }

  async getTrendCategoryJudges(id: string): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      `/civil-lawsuits/trend-categories/${id}/judges`
    );
    return response.data;
  }

  async getTrendCategoryFacets(id: string, request: {
    courtStates?: string[];
    courtNames?: string[];
    defendants?: string[];
    plaintiffLawyerFirms?: string[];
    filedAtFrom?: string;
    filedAtTo?: string;
  }): Promise<{
    courtStates: Array<{ value: string; count: number }>;
    courtNames: Array<{ value: string; count: number }>;
    defendants: Array<{ value: string; count: number }>;
    plaintiffLawyerFirms: Array<{ value: string; count: number }>;
    minFiledAt?: string;
    maxFiledAt?: string;
  }> {
    const response = await authenticatedAxios.post(
      `/civil-lawsuits/trend-categories/${id}/facets`,
      request
    );
    return response.data;
  }

  async getTrendCategoryList(id: string, request: {
    filters: {
      courtStates?: string[];
      courtNames?: string[];
      defendants?: string[];
      plaintiffLawyerFirms?: string[];
      filedAtFrom?: string;
      filedAtTo?: string;
    };
    page: number;
    pageSize: number;
  }): Promise<CivilLawsuitBucketResponse> {
    const response = await authenticatedAxios.post(
      `/civil-lawsuits/trend-categories/${id}/list`,
      {
        ...request.filters,
        page: request.page,
        pageSize: request.pageSize
      }
    );
    return response.data;
  }

  async getCivilLawsuitCourts(): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      '/search/civil-lawsuits/trends/courts'
    );
    return response.data;
  }

  async getCivilLawsuitJudges(): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      '/search/civil-lawsuits/trends/judges'
    );
    return response.data;
  }

  async getCivilLawsuitStates(): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      '/search/civil-lawsuits/trends/states'
    );
    return response.data;
  }

  async getCivilLawsuitNaturesOfSuit(): Promise<string[]> {
    const response = await authenticatedAxios.get<string[]>(
      '/search/civil-lawsuits/trends/natures-of-suit'
    );
    return response.data;
  }

  async getCivilLawsuitsFacets(request: {
    query?: string;
    embeddingFields?: string[];
    mode?: string;
    knnK?: number;
    knnNumCandidates?: number;
    maxChunks?: number;
    courtStates?: string[];
    courtName?: string;
    defendants?: string[];
    plaintiffLawyerFirms?: string[];
    filedAtFrom?: string;
    filedAtTo?: string;
    signal?: AbortSignal;
  }): Promise<{
    courtStates: Array<{ value: string; count: number }>;
    defendants: Array<{ value: string; count: number }>;
    plaintiffLawyerFirms: Array<{ value: string; count: number }>;
    minFiledAt?: string;
    maxFiledAt?: string;
  }> {
    const response = await authenticatedAxios.post(
      '/search/civil-lawsuits/trends/facets',
      {
        query: request.query || null,
        embeddingFields: request.embeddingFields,
        mode: request.mode || 'vector',
        knnK: request.knnK || null,
        knnNumCandidates: request.knnNumCandidates || null,
        maxChunks: request.maxChunks || null,
        courtStates: request.courtStates,
        courtName: request.courtName,
        defendants: request.defendants,
        plaintiffLawyerFirms: request.plaintiffLawyerFirms,
        filedAtFrom: request.filedAtFrom,
        filedAtTo: request.filedAtTo
      },
      { signal: request.signal }
    );
    return response.data;
  }

  // ==================== Session Memory ====================

  async getSessionMemory(sessionId: string): Promise<{ exists: boolean; memory?: any }> {
    const response = await authenticatedAxios.get(`/api/session-memory/${sessionId}`);
    return response.data;
  }

  // ==================== User Profile ====================

  async getUserProfile(): Promise<{ exists: boolean; profile?: any; userId?: string }> {
    const response = await authenticatedAxios.get('/api/user-profile');
    return response.data;
  }

  async saveUserProfile(profile: { defaultJurisdiction?: string; expertise?: string; preferredDepth?: string; practiceAreas?: string[] }): Promise<{ success: boolean; profile: any }> {
    const response = await authenticatedAxios.put('/api/user-profile', profile);
    return response.data;
  }

  async deleteUserProfile(): Promise<{ success: boolean }> {
    const response = await authenticatedAxios.delete('/api/user-profile');
    return response.data;
  }

  logout(): void {
    logoutUtil();
  }
}